import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { CheckCircleTwoTone, CloseCircleTwoTone } from "@ant-design/icons";
import { LearnMoreTag, useCookieExpertsModal } from "@devowl-wp/react-cookie-banner-admin";
import { Row, Col, Divider } from "antd";
import { useStores } from "../store";
import { __, _i } from "../utils";
import moment from "moment";
function useConfigHintText(identifier) {
    for(var _len = arguments.length, sprintfArgs = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++){
        sprintfArgs[_key - 1] = arguments[_key];
    }
    const { optionStore: { isTcf, consentsDeletedAt, consentDuration } } = useStores();
    switch(identifier){
        case "scanner":
            {
                const { openDialog } = useCookieExpertsModal();
                return /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx("p", {
                            className: "description",
                            children: __("The scanner finds services that you use on your website that might set/read cookies or process personal data. This is e.g. Google Analytics, YouTube or Elementor. If there is no template for a service, you will see from which external URLs content, scripts etc. are embedded. This allows you to set up your cookie banner quickly and easily.")
                        }),
                        /*#__PURE__*/ _jsx("p", {
                            className: "description",
                            children: _i(__("We explicitly do not find cookies because that would not work reliably. {{a}}We explained why in our knowledge base.{{/a}}"), {
                                a: /*#__PURE__*/ _jsx("a", {
                                    rel: "noreferrer",
                                    href: __("https://devowl.io/knowledge-base/real-cookie-banner-cookie-scanner-finds-cookies-automatically/"),
                                    target: "_blank"
                                })
                            })
                        }),
                        /*#__PURE__*/ _jsxs(Row, {
                            style: {
                                margin: "10px 0"
                            },
                            children: [
                                /*#__PURE__*/ _jsx(Col, {
                                    span: 11,
                                    children: /*#__PURE__*/ _jsxs("div", {
                                        style: {
                                            paddingRight: 10
                                        },
                                        children: [
                                            /*#__PURE__*/ _jsx(Divider, {
                                                children: __("What the scanner finds ...")
                                            }),
                                            [
                                                __("External services (with and without template)"),
                                                __("WordPress plugins with templates that require consent"),
                                                __("Automatic check of all subpages of your website")
                                            ].map((text, i)=>/*#__PURE__*/ _jsxs("div", {
                                                    style: {
                                                        marginBottom: 10
                                                    },
                                                    children: [
                                                        /*#__PURE__*/ _jsx(CheckCircleTwoTone, {
                                                            twoToneColor: "#52c41a"
                                                        }),
                                                        "\xa0\xa0",
                                                        text
                                                    ]
                                                }, i))
                                        ]
                                    })
                                }),
                                /*#__PURE__*/ _jsx(Col, {
                                    span: 2,
                                    style: {
                                        textAlign: "center"
                                    },
                                    children: /*#__PURE__*/ _jsx(Divider, {
                                        type: "vertical",
                                        style: {
                                            height: "100%"
                                        }
                                    })
                                }),
                                /*#__PURE__*/ _jsx(Col, {
                                    span: 11,
                                    children: /*#__PURE__*/ _jsxs("div", {
                                        children: [
                                            /*#__PURE__*/ _jsx(Divider, {
                                                children: __("... and what it does not")
                                            }),
                                            [
                                                __("Cookies from unknown WordPress plugins"),
                                                __("Services embedded after the page load via JavaScript"),
                                                __("Complete coverage of your individual use case")
                                            ].map((text, i)=>/*#__PURE__*/ _jsxs("div", {
                                                    style: {
                                                        marginBottom: 10
                                                    },
                                                    children: [
                                                        /*#__PURE__*/ _jsx(CloseCircleTwoTone, {
                                                            twoToneColor: "#eb2f96"
                                                        }),
                                                        "\xa0\xa0",
                                                        text
                                                    ]
                                                }, i))
                                        ]
                                    })
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx("p", {
                            className: "description",
                            children: _i(__("Just by using the scanner, you will not set up your cookie banner one hundred percent correctly. If it is too complex or time-consuming for you to set up the cookie banner yourself, just let one of our {{a}}cookie experts{{/a}} set it up for you!"), {
                                a: /*#__PURE__*/ _jsx("a", {
                                    onClick: openDialog
                                })
                            })
                        })
                    ]
                });
            }
        case "cookie":
            return /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    __("What are services? Services can be external applications such as Google Analytics or WordPress plugins or themes that process personal data (e.g. IP address) and/or set cookies. Cookies (and similar technologies) are small text files that are stored on the device of visitors to your website. You can store information about the visitor in cookies, such as the website's language, or unique advertising IDs to display personalized advertising. You, as the site owner, must ensure that cookies are only placed on your visitors' devices and personal data are only processed if they have given their explicit consent. Unless you have a legitimate interest in the legal sense to do so even without consent. You can define here all the services you use and their cookies with their legal and technical information."),
                    "\xa0",
                    /*#__PURE__*/ _jsx(LearnMoreTag, {
                        url: __("https://devowl.io/2021/web-cookies-overview/")
                    })
                ]
            });
        case "blocker":
            return __("What is a content blocker? Imagine that a user of your website does not accept all services. At the same time, you have integrated e.g. a YouTube video that sets cookies that the visitor has not agreed to. According to the ePrivacy Directive, this is prohibited. Content blockers automatically replace iframes, script and link tags like YouTube videos for such users and offer them to watch the video as soon as they agree to load it.");
        case "list-of-consents":
            return _i(__("Consents are automatically documented in order to be able to prove compliance with the legal requirements according to {{a}}Art. 5 GDPR{{/a}} and, in case of dispute, to prove how the consent was obtained."), {
                a: /*#__PURE__*/ _jsx("a", {
                    href: __("https://gdpr-text.com/read/article-5/"),
                    target: "_blank",
                    rel: "noreferrer"
                })
            });
        case "consents-deleted":
            return /*#__PURE__*/ _jsx(_Fragment, {
                children: consentsDeletedAt ? __("Consents before %s has been automatically deleted according to the settings you have made.", moment(consentsDeletedAt).subtract(consentDuration, "months").toDate().toLocaleString(document.documentElement.lang)) : null
            });
        case "shortcodes":
            return /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    _i(__("Your website visitors must be able to view their consent history, change their consent, or withdraw their consent at any time. This must be as easy as giving consent. Therefore, the legal links must be included on every subpage of the website (e.g. in the footer)."), {
                        strong: /*#__PURE__*/ _jsx("strong", {})
                    }),
                    /*#__PURE__*/ _jsx("br", {}),
                    /*#__PURE__*/ _jsx("br", {}),
                    _i(__("Hiding these options, e.g. in the privacy policy, is in the opinion of multiple data protection authorities in the EU a violation of the GDPR."), {
                        a: /*#__PURE__*/ _jsx("a", {
                            href: __("https://www.datenschutzkonferenz-online.de/media/oh/20211220_oh_telemedien.pdf"),
                            target: "_blank",
                            rel: "noreferrer"
                        })
                    }),
                    isTcf ? /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx("br", {}),
                            /*#__PURE__*/ _jsx("br", {}),
                            __("To meet the requirements of the TCF standard, the shortcodes should be placed near the link to the privacy policy.")
                        ]
                    }) : null
                ]
            });
        case "tcf-vendor":
            return _i(__("What is a TCF vendor? According to the IAB Europe Transparency & Consent Framework (TCF), any service (e.g. Google for Google Ads) that wants to use consents according to the TCF standard must register as a vendor in the {{a}}Global Vendor List (GVL){{/a}}. All TCF vendors specify for which purposes they need consent to process data and set cookies and which features they can offer with these consents. They also provide a link to their privacy policy for further information. You, as a website operator, must obtain consent in your cookie banner for all vendors you work with. You can limit the requested purposes of vendors to keep consents as privacy-friendly as possible."), {
                a: /*#__PURE__*/ _jsx("a", {
                    href: __("https://iabeurope.eu/vendor-list-tcf/"),
                    target: "_blank",
                    rel: "noreferrer"
                })
            });
        case "tcf-integration":
            return /*#__PURE__*/ _jsx(_Fragment, {
                children: __("You are trying to obtain consent for the advertising network %s. In order to be able to display advertising, you must obtain consent in accordance with the TCF standard. Therefore, please first activate TCF compatibility for your cookie banner so that you can then make all the necessary configurations!", ...sprintfArgs)
            });
        case "import":
            return /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    __("You can export and import all or only some of the settings you made in Real Cookie Banner. If you have several websites, you can save a lot of time by transferring the settings comfortably."),
                    /*#__PURE__*/ _jsx("br", {}),
                    /*#__PURE__*/ _jsx("br", {}),
                    __("Also, you can export documented consents to save them in a local backup.")
                ]
            });
        default:
            return "";
    }
}
export { useConfigHintText };
